import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  AlertTriangle,
  Plus,
  Pencil,
  Trash2,
  Eye,
  ShieldAlert,
  Zap,
  Cross,
  CheckCircle,
  Clock,
  FileText
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = { accident: '事故報告', near_miss: 'ヒヤリハット' };
const SEVERITY_LABELS = { critical: '重大', moderate: '中程度', minor: '軽微' };
const STATUS_LABELS = { draft: '下書き', reported: '報告済', in_progress: '対応中', completed: '完了' };

const SEVERITY_COLORS = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  moderate: 'bg-amber-100 text-amber-800 border-amber-200',
  minor: 'bg-blue-100 text-blue-800 border-blue-200'
};

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700 border-gray-200',
  reported: 'bg-sky-100 text-sky-800 border-sky-200',
  in_progress: 'bg-orange-100 text-orange-800 border-orange-200',
  completed: 'bg-emerald-100 text-emerald-800 border-emerald-200'
};

export default function IncidentsPage() {
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchData();
  }, [filterType, filterSeverity, filterStatus]);

  const fetchData = async () => {
    try {
      const params = {};
      if (filterType !== 'all') params.incident_type = filterType;
      if (filterSeverity !== 'all') params.severity = filterSeverity;
      if (filterStatus !== 'all') params.status = filterStatus;

      const [incRes, statsRes] = await Promise.all([
        axios.get(`${API}/incidents`, { params }),
        axios.get(`${API}/incidents/stats`)
      ]);
      setIncidents(incRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('データ取得に失敗', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/incidents/${id}`);
      fetchData();
    } catch (error) {
      console.error('削除に失敗', error);
    }
  };

  const clearFilters = () => {
    setFilterType('all');
    setFilterSeverity('all');
    setFilterStatus('all');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="incidents-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            インシデント管理
          </h1>
          <p className="text-muted-foreground mt-1">
            事故報告・ヒヤリハット: {stats?.total || 0}件
          </p>
        </div>
        <Button className="btn-active" onClick={() => navigate('/incidents/new')} data-testid="new-incident-button">
          <Plus size={18} className="mr-2" />
          新規報告
        </Button>
      </div>

      {/* 統計サマリー */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          <Card className="card-surface text-center">
            <CardContent className="pt-4 pb-3">
              <ShieldAlert size={22} className="mx-auto mb-1 text-red-500" />
              <p className="text-xl font-bold">{stats.by_type.accident}</p>
              <p className="text-xs text-muted-foreground">事故報告</p>
            </CardContent>
          </Card>
          <Card className="card-surface text-center">
            <CardContent className="pt-4 pb-3">
              <Zap size={22} className="mx-auto mb-1 text-amber-500" />
              <p className="text-xl font-bold">{stats.by_type.near_miss}</p>
              <p className="text-xs text-muted-foreground">ヒヤリハット</p>
            </CardContent>
          </Card>
          <Card className="card-surface text-center">
            <CardContent className="pt-4 pb-3">
              <AlertTriangle size={22} className="mx-auto mb-1 text-red-600" />
              <p className="text-xl font-bold">{stats.by_severity.critical}</p>
              <p className="text-xs text-muted-foreground">重大</p>
            </CardContent>
          </Card>
          <Card className="card-surface text-center">
            <CardContent className="pt-4 pb-3">
              <FileText size={22} className="mx-auto mb-1 text-gray-400" />
              <p className="text-xl font-bold">{stats.by_status.draft}</p>
              <p className="text-xs text-muted-foreground">下書き</p>
            </CardContent>
          </Card>
          <Card className="card-surface text-center">
            <CardContent className="pt-4 pb-3">
              <Eye size={22} className="mx-auto mb-1 text-sky-500" />
              <p className="text-xl font-bold">{stats.by_status.reported}</p>
              <p className="text-xs text-muted-foreground">報告済</p>
            </CardContent>
          </Card>
          <Card className="card-surface text-center">
            <CardContent className="pt-4 pb-3">
              <Clock size={22} className="mx-auto mb-1 text-orange-500" />
              <p className="text-xl font-bold">{stats.by_status.in_progress}</p>
              <p className="text-xs text-muted-foreground">対応中</p>
            </CardContent>
          </Card>
          <Card className="card-surface text-center">
            <CardContent className="pt-4 pb-3">
              <CheckCircle size={22} className="mx-auto mb-1 text-emerald-500" />
              <p className="text-xl font-bold">{stats.by_status.completed}</p>
              <p className="text-xs text-muted-foreground">完了</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* フィルター */}
      <Card className="card-surface">
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="w-48">
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger data-testid="filter-type">
                  <SelectValue placeholder="種別" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">すべての種別</SelectItem>
                  <SelectItem value="accident">事故報告</SelectItem>
                  <SelectItem value="near_miss">ヒヤリハット</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Select value={filterSeverity} onValueChange={setFilterSeverity}>
                <SelectTrigger data-testid="filter-severity">
                  <SelectValue placeholder="重大度" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">すべての重大度</SelectItem>
                  <SelectItem value="critical">重大</SelectItem>
                  <SelectItem value="moderate">中程度</SelectItem>
                  <SelectItem value="minor">軽微</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger data-testid="filter-status">
                  <SelectValue placeholder="ステータス" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">すべてのステータス</SelectItem>
                  <SelectItem value="draft">下書き</SelectItem>
                  <SelectItem value="reported">報告済</SelectItem>
                  <SelectItem value="in_progress">対応中</SelectItem>
                  <SelectItem value="completed">完了</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={clearFilters} data-testid="clear-filters">
              フィルタークリア
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 一覧 */}
      {incidents.length === 0 ? (
        <Card className="card-surface">
          <CardContent className="py-12 text-center">
            <ShieldAlert size={48} className="mx-auto mb-4 text-muted-foreground/50" />
            <p className="text-muted-foreground">インシデント報告がありません</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate('/incidents/new')} data-testid="empty-new-button">
              最初の報告を作成する
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {incidents.map((inc) => (
            <Card key={inc.id} className="card-surface hover:shadow-md transition-shadow" data-testid={`incident-card-${inc.id}`}>
              <CardContent className="py-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {inc.incident_type === 'accident' ? (
                        <ShieldAlert size={18} className="text-red-500 shrink-0" />
                      ) : (
                        <Zap size={18} className="text-amber-500 shrink-0" />
                      )}
                      <span className="font-semibold text-foreground truncate">{inc.title}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${SEVERITY_COLORS[inc.severity]}`}>
                        {SEVERITY_LABELS[inc.severity]}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_COLORS[inc.status]}`}>
                        {STATUS_LABELS[inc.status]}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                      <span>{TYPE_LABELS[inc.incident_type]}</span>
                      {inc.client_name && <span>対象: {inc.client_name}</span>}
                      <span>{inc.occurred_at?.split('T')[0]}</span>
                      {inc.location && <span>{inc.location}</span>}
                      <span>報告者: {inc.reported_by_name}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => navigate(`/incidents/${inc.id}/edit`)} data-testid={`edit-incident-${inc.id}`}>
                      <Pencil size={14} className="mr-1" />
                      編集
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" data-testid={`delete-incident-${inc.id}`}>
                          <Trash2 size={14} />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>インシデント報告を削除しますか？</AlertDialogTitle>
                          <AlertDialogDescription>この操作は取り消せません。</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>キャンセル</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(inc.id)}>削除する</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
